import { Col, Row } from "react-bootstrap"
import React from "react"
import { MatchScoreboard } from "@components/Match/MatchScoreboard/MatchScoreboard"
import { MatchMenu } from "@components/Match/Matchmenu/MatchMenu"
import { MatchLiveTicker } from "@components/Match/MatchLiveTicker/MatchLiveTicker"
import { MatchAPI } from "../../../src/apis/MatchAPI"
import { getNavigationProps } from "@components/Navigation/getNavigationProps"
import { SEO } from "../../../src/types/next.js"
import { ParsedUrlQuery } from "querystring"
import { GetStaticPropsContext } from "next"
import InferNextPropsType from "infer-next-props-type"
import * as NextConstants from "../../../src/constants/next"
import { TeamAPI } from "../../../src/apis/TeamAPI"
import { getMatchCanonicalData } from "utils/match"
import { MatchTeamPlayers } from "@components/Match/MatchTeamPlayers/MatchTeamPlayers"
import { GroupStandings } from "@components/Competition/GroupStandings"
import { GroupAPI } from "../../../src/apis/GroupAPI"
import { simplifyNews } from "utils/news"

export default function MatchPage({
  matchCalciocomSlug,
  match,
  events,
  homeTeamLastMatchesPlayed,
  awayTeamLastMatchesPlayed,
  matchTeamPlayers,
  teamStandings,
  prediction,
  isStandingsAvailable,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <>
      <Row className={"marginZero"}>
        <Col className={"paddingZero"}>
          <div id="hs-content" className={"hs-border-div paddingZero"}>
            <MatchScoreboard
              match={match}
              events={events}
              homeTeamLastMatchesPlayed={homeTeamLastMatchesPlayed}
              awayTeamLastMatchesPlayed={awayTeamLastMatchesPlayed}
              prediction={prediction}
            />
            <div className="paddingOFive">
              <MatchMenu
                isStandingsAvailable={isStandingsAvailable}
                selected="diretta-live"
                matchCalciocomSlug={matchCalciocomSlug}
                match={match}
              />
            </div>
            <MatchLiveTicker match={match} events={events} />
            {matchTeamPlayers.awayTeamPlayers.length > 0 && matchTeamPlayers.homeTeamPlayers.length > 0 && (
              <MatchTeamPlayers match={match} matchTeamPlayers={matchTeamPlayers} />
            )}
            {teamStandings && teamStandings.length > 0 && (
              <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <GroupStandings
                  competitionCalciocomSlug={match.competition?.calciocomSlug || null}
                  seasonCalciocomSlug={match.season?.calciocomSlug || null}
                  teamStandings={teamStandings}
                  homeTeamId={match.homeTeam?.id}
                  awayTeamId={match.awayTeam?.id}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

interface IParams extends ParsedUrlQuery {
  matchCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>) {
  try {
    const { matchCalciocomSlug } = context.params!

    const [match] = await Promise.all([MatchAPI.getMatchByCalciocomSlug(matchCalciocomSlug)])

    const [
      events,
      matchTeamPlayers,
      homeTeamLastMatchesPlayed,
      awayTeamLastMatchesPlayed,
      navigationProps,
      teamStandings,
      prediction,
      isStandingsAvailable,
    ] = await Promise.all([
      MatchAPI.getMatchEvents(match.id),
      MatchAPI.getMatchTeamPlayers(match.id),
      TeamAPI.getLastMatchesPlayed(match.homeTeamId!, match.groupId!),
      TeamAPI.getLastMatchesPlayed(match.awayTeamId!, match.groupId!),
      getNavigationProps(match.competition?.id),
      GroupAPI.getTeamBettingParameterStandings(match.groupId),
      MatchAPI.getPredictionByMatchId(match.id),
      GroupAPI.getIsStandingsAvailable(match.groupId, match.turnId),
    ])

    const turnName = match.turn?.name.split(".") ? `${match.turn?.name.split(".")[0]}º giornata` : match.turn?.name

    const opt: Intl.DateTimeFormatOptions = {
      timeZone: "Europe/Rome",
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
    const formatDate = match.date ? new Date(match.date).toLocaleString("it-IT", opt) : ""
    const title = `${match.homeTeam?.name} - ${match.awayTeam?.name}: risultati in tempo reale ${formatDate}`
    const seo: SEO = {
      title: title,
      description: `Segui la diretta live di ${match.homeTeam?.name} - ${match.awayTeam?.name}, valida per la ${turnName} di ${match.competition?.name} ${match.season?.name} | ⭐ Risultati in diretta ✔️ Statistiche`,
      keywords: [
        "Diretta scritta",
        `${match.homeTeam?.name}`,
        `${match.awayTeam?.name}`,
        `${match.competition?.name}`,
        `${match.season?.name}`,
        `${match.turn?.name}`,
      ],
      ...(await getMatchCanonicalData(match, `/partite/${matchCalciocomSlug}/diretta-live`)),
    }
    return {
      props: {
        matchCalciocomSlug,
        match,
        events,
        homeTeamLastMatchesPlayed,
        awayTeamLastMatchesPlayed,
        matchTeamPlayers,
        navigationProps: navigationProps,
        title,
        seo,
        teamStandings,
        prediction: prediction ? simplifyNews(prediction) : null,
        isStandingsAvailable,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
